import { say } from "../../localization/say.ts";
import { read, upload } from "../../net/api.ts";
import type { Began, Dictation, DictationCallbacks } from "./dictation.ts";
import { recordedWav } from "./dictation-wav.ts";

export type DictationMode = {
  available: boolean;
  maxSeconds: number;
  languages: string[];
  processor: "local" | "private-remote" | "venice";
};

export const SILENCE_MS = 700;

export function silenceEnded(input: {
  heardSpeech: boolean;
  level: number;
  quietSince: number | null;
  now: number;
  threshold?: number;
}): { ended: boolean; heardSpeech: boolean; quietSince: number | null } {
  const threshold = input.threshold ?? 0.025;
  if (input.level >= threshold) return { ended: false, heardSpeech: true, quietSince: null };
  if (!input.heardSpeech) return { ended: false, heardSpeech: false, quietSince: null };
  const quietSince = input.quietSince ?? input.now;
  return { ended: input.now - quietSince >= SILENCE_MS, heardSpeech: true, quietSince };
}

export function recordingType(recorder: typeof MediaRecorder): string {
  const offered = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/mp4"];
  return offered.find((type) => recorder.isTypeSupported(type)) ?? "";
}

export function browserDictation(): Dictation {
  let recorder: MediaRecorder | null = null;
  let stream: MediaStream | null = null;
  let context: AudioContext | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;
  let chunks: Blob[] = [];
  let mode: DictationMode | null = null;
  let callbacks: DictationCallbacks = {};
  let generation = 0;
  let busy = false;
  let disclosed = false;
  let request: AbortController | null = null;

  const clean = async () => {
    if (timer) clearInterval(timer);
    timer = null;
    stream?.getTracks().forEach((track) => {
      track.stop();
    });
    stream = null;
    recorder = null;
    callbacks.onLevel?.(0);
    if (context) await context.close().catch(() => undefined);
    context = null;
  };

  const finish = async (deliver = false): Promise<string> => {
    if (busy) return "";
    const active = recorder;
    if (!active || active.state === "inactive") return "";
    busy = true;
    const own = generation;
    const told = callbacks;
    return new Promise<string>((resolve) => {
      active.addEventListener(
        "stop",
        () => {
          const recorded = new Blob(chunks, { type: active.mimeType || "audio/webm" });
          chunks = [];
          void clean().then(async () => {
            if (own !== generation) {
              busy = false;
              resolve("");
              return;
            }
            request = new AbortController();
            try {
              const bytes = await recordedWav(recorded);
              if (own !== generation) return resolve("");
              const browserLanguage = (navigator.language ?? "").split("-")[0]?.toLowerCase() ?? "";
              const asked: Record<string, string> = mode?.languages.includes(browserLanguage)
                ? { language: browserLanguage }
                : {};
              const answer = await upload<{ text: string }>(
                "dictation.transcribe",
                asked,
                bytes,
                request.signal,
              );
              if (own !== generation) return resolve("");
              const text = answer.text.trim();
              if (deliver) told.onFinished?.(text);
              resolve(text);
            } catch {
              if (own === generation) told.onFailed?.(say("dictation.failed"));
              resolve("");
            } finally {
              request = null;
              busy = false;
            }
          });
        },
        { once: true },
      );
      active.stop();
    });
  };

  return {
    start: async (next = {}): Promise<Began> => {
      if (busy || recorder?.state === "recording") return { started: false, why: say("dictation.busy") };
      busy = true;
      const own = ++generation;
      chunks = [];
      callbacks = next;
      try {
        mode = await read<DictationMode>("dictation.mode");
        if (own !== generation) return { started: false, why: say("dictation.closed") };
        if (!mode.available) return { started: false, why: say("dictation.none") };
        if (mode.processor !== "local" && !disclosed) {
          disclosed = true;
          return {
            started: false,
            why: say(mode.processor === "venice" ? "dictation.externalVenice" : "dictation.externalPrivate"),
          };
        }
        const heard = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (own !== generation) {
          heard.getTracks().forEach((track) => {
            track.stop();
          });
          return { started: false, why: say("dictation.closed") };
        }
        stream = heard;
        const type = recordingType(MediaRecorder);
        const active = new MediaRecorder(heard, type ? { mimeType: type } : undefined);
        recorder = active;
        active.addEventListener("dataavailable", (event) => {
          if (own === generation && event.data.size > 0) chunks.push(event.data);
        });

        context = new AudioContext();
        const source = context.createMediaStreamSource(heard);
        const analyser = context.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        const values = new Uint8Array(analyser.fftSize);
        let heardSpeech = false;
        let quietSince: number | null = null;
        const beganAt = Date.now();
        const maxMilliseconds = mode.maxSeconds * 1000;
        active.start(200);
        timer = setInterval(() => {
          analyser.getByteTimeDomainData(values);
          let sum = 0;
          for (const value of values) {
            const centred = (value - 128) / 128;
            sum += centred * centred;
          }
          const level = Math.sqrt(sum / values.length);
          next.onLevel?.(Math.min(1, level * 8));
          const silence = silenceEnded({ heardSpeech, level, quietSince, now: Date.now() });
          heardSpeech = silence.heardSpeech;
          quietSince = silence.quietSince;
          if (silence.ended || Date.now() - beganAt >= maxMilliseconds) void finish(true);
        }, 50);
        return { started: true };
      } catch (cause) {
        await clean();
        const denied = cause instanceof DOMException && cause.name === "NotAllowedError";
        return {
          started: false,
          why: denied ? say("dictation.permissionRefused") : say("dictation.couldNotStart"),
        };
      } finally {
        busy = false;
      }
    },
    stop: () => finish(false),
    discard: () => {
      generation += 1;
      callbacks = {};
      request?.abort();
      request = null;
      const active = recorder;
      chunks = [];
      if (active?.state === "recording") active.stop();
      void clean();
      busy = false;
    },
  };
}
